import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  CalendarPlus, 
  FileText, 
  Receipt, 
  Star, 
  Archive, 
  ArrowRight,
  CheckCircle,
  X
} from 'lucide-react';

/**
 * WhatsNextPrompt - A reusable "What would you like to do next?" modal
 * 
 * Props:
 * - open: boolean - controls visibility
 * - onOpenChange: function - called when modal should close
 * - title: string - main title (e.g., "Customer Created!")
 * - subtitle: string - description text
 * - icon: React component - icon to show (optional)
 * - iconBgColor: string - background color for icon (e.g., "bg-green-100")
 * - iconColor: string - icon color (e.g., "text-green-600")
 * - actions: array of { label, description, icon, onClick, variant }
 * - dismissLabel: string - label for dismiss button (default: "Maybe Later")
 */
const WhatsNextPrompt = ({ 
  open, 
  onOpenChange, 
  title,
  subtitle,
  icon: IconComponent = CheckCircle,
  iconBgColor = "bg-green-100",
  iconColor = "text-green-600",
  actions = [],
  dismissLabel = "Maybe Later"
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader className="text-center pb-2">
          <div className={`w-16 h-16 ${iconBgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <IconComponent className={`w-8 h-8 ${iconColor}`} />
          </div>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          {subtitle && (
            <DialogDescription className="text-muted-foreground mt-1">
              {subtitle}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-2 mt-4">
          <p className="text-sm font-medium text-muted-foreground text-center mb-3">
            What would you like to do next?
          </p>
          
          {actions.map((action, index) => {
            const ActionIcon = action.icon || ArrowRight;
            return (
              <button
                key={index}
                onClick={() => {
                  action.onClick();
                  onOpenChange(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-primary hover:bg-primary/5 transition-all group text-left"
                data-testid={`whats-next-action-${index}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${action.iconBg || 'bg-gray-100'} group-hover:bg-primary/10 transition-colors`}>
                  <ActionIcon className={`w-5 h-5 ${action.iconColor || 'text-gray-600'} group-hover:text-primary transition-colors`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{action.label}</p>
                  {action.description && (
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t">
          <Button 
            variant="ghost" 
            className="w-full text-muted-foreground hover:text-foreground"
            onClick={() => onOpenChange(false)}
            data-testid="whats-next-dismiss"
          >
            {dismissLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Pre-configured prompts for common scenarios
export const CustomerCreatedPrompt = ({ open, onOpenChange, customerName, onScheduleVisit, onCreateEstimate }) => (
  <WhatsNextPrompt
    open={open}
    onOpenChange={onOpenChange}
    title="Customer Created!"
    subtitle={`${customerName} has been added to your customer list.`}
    iconBgColor="bg-green-100"
    iconColor="text-green-600"
    actions={[
      {
        label: "Schedule Site Visit",
        description: "Book an on-site assessment",
        icon: CalendarPlus,
        iconBg: "bg-blue-50",
        iconColor: "text-blue-600",
        onClick: onScheduleVisit
      },
      {
        label: "Create Estimate",
        description: "Start a new quote for this customer",
        icon: FileText,
        iconBg: "bg-emerald-50",
        iconColor: "text-emerald-600",
        onClick: onCreateEstimate
      }
    ]}
    dismissLabel="Back to Customers"
  />
);

export const JobCompletedPrompt = ({ open, onOpenChange, jobTitle, onCreateInvoice, onRequestReview }) => (
  <WhatsNextPrompt
    open={open}
    onOpenChange={onOpenChange}
    title="Job Completed!"
    subtitle={`"${jobTitle}" has been marked as completed.`}
    iconBgColor="bg-green-100"
    iconColor="text-green-600"
    actions={[
      {
        label: "Create Invoice",
        description: "Bill the customer for this job",
        icon: Receipt,
        iconBg: "bg-emerald-50",
        iconColor: "text-emerald-600",
        onClick: onCreateInvoice
      },
      ...(onRequestReview ? [{
        label: "Request Review",
        description: "Ask for a Google/Yelp review",
        icon: Star,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        onClick: onRequestReview
      }] : [])
    ]}
    dismissLabel="Close"
  />
);

export const InvoicePaidPrompt = ({ open, onOpenChange, invoiceNumber, customerName, onArchiveCustomer, onRequestReview }) => (
  <WhatsNextPrompt
    open={open}
    onOpenChange={onOpenChange}
    title="Invoice Paid!"
    subtitle={`Invoice ${invoiceNumber} from ${customerName} has been fully paid.`}
    iconBgColor="bg-green-100"
    iconColor="text-green-600"
    actions={[
      ...(onRequestReview ? [{
        label: "Request Review",
        description: "Happy customers leave great reviews!",
        icon: Star,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        onClick: onRequestReview
      }] : []),
      ...(onArchiveCustomer ? [{
        label: "Archive Customer",
        description: "Move to archived list",
        icon: Archive,
        iconBg: "bg-gray-100",
        iconColor: "text-gray-600",
        onClick: onArchiveCustomer
      }] : [])
    ]}
    dismissLabel="Done"
  />
);

export default WhatsNextPrompt;
